import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const OAuthCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('processing');
    const [error, setError] = useState(null);

    useEffect(() => {
        const handleCallback = async () => {
            // Log all URL parameters for debugging
            console.log('OAuth Callback - URL params:', {
                provider: searchParams.get('provider'),
                error: searchParams.get('error'),
                code: searchParams.get('code') ? 'present' : 'missing',
                state: searchParams.get('state') ? 'present' : 'missing',
                allParams: Object.fromEntries(searchParams.entries())
            });
            
            const provider = searchParams.get('provider');
            const errorParam = searchParams.get('error');
            const code = searchParams.get('code');
            const state = searchParams.get('state');

            // If we have a code but no provider, this might be a direct callback from OAuth provider
            // This shouldn't happen (backend should handle it), but let's handle it gracefully
            if (code && !provider && !errorParam) {
                console.warn('Received OAuth code directly in frontend - this should be handled by backend callback');
                setStatus('error');
                setError('OAuth callback received incorrectly. Please try connecting again.');
                setTimeout(() => {
                    navigate('/profile?tab=account', { replace: true });
                }, 4000);
                return;
            }

            // Handle error case (from /oauth/error route or error parameter)
            if (errorParam) {
                // Map common OAuth error codes to user-friendly messages
                let errorMessage = 'An error occurred during connection. Please try again.';
                if (errorParam === 'access_denied') {
                    errorMessage = 'You cancelled the connection. No account was connected.';
                } else if (errorParam === 'invalid_request') {
                    errorMessage = 'Invalid OAuth request. Please try again or contact support.';
                } else if (errorParam === 'server_error') {
                    errorMessage = 'Server error during connection. Please try again later.';
                } else if (errorParam.includes('state')) {
                    errorMessage = 'Security validation failed. Please try connecting again.';
                } else {
                    // Try to decode error message if it's URL encoded
                    try {
                        const decoded = decodeURIComponent(errorParam);
                        if (decoded && decoded.length < 200) {
                            errorMessage = decoded;
                        }
                    } catch (e) {
                        // Keep default message if decoding fails
                    }
                }
                
                // Show the error state briefly, then redirect to the Account
                // page which also displays the error message
                setStatus('error');
                setError(errorMessage);
                const errorProvider = searchParams.get('provider') || 'account';
                await new Promise(resolve => setTimeout(resolve, 2000));
                navigate(`/profile?tab=account&oauth=error&error=${encodeURIComponent(errorMessage)}&provider=${errorProvider}`, { replace: true });
                return;
            }

            // Handle success case (from /oauth/success route)
            if (provider) {
                // Show the success state while giving the backend a moment
                // to finish processing the callback, then redirect
                setStatus('success');
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate(`/profile?tab=account&oauth=success&provider=${provider}`, { replace: true });
            } else {
                // No provider parameter - redirect to profile
                navigate('/profile?tab=account', { replace: true });
            }
        };

        handleCallback();
    }, [searchParams, navigate]);

    return (
        <div style={{
            minHeight: '100vh',
            width: '100vw',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            color: '#fff',
            fontFamily: 'Poppins, Arial, sans-serif',
        }}>
            {status === 'processing' && (
                <>
                    <div style={{
                        width: 48,
                        height: 48,
                        border: '4px solid rgba(96,165,250,0.2)',
                        borderTop: '4px solid #60a5fa',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: 24,
                    }} />
                    <div style={{ color: '#b6c6e3', fontSize: 18 }}>
                        Connecting your account...
                    </div>
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </>
            )}
            
            {status === 'success' && (
                <>
                    <div style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: 'rgba(34,197,94,0.2)',
                        border: '2px solid #22c55e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 24,
                    }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3">
                            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <div style={{ color: '#22c55e', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                        Account Connected Successfully!
                    </div>
                    <div style={{ color: '#b6c6e3', fontSize: 14 }}>
                        Redirecting to your profile...
                    </div>
                </>
            )}

            {status === 'error' && (
                <>
                    <div style={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: 'rgba(239,68,68,0.2)',
                        border: '2px solid #ef4444',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 24,
                    }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3">
                            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <div style={{ color: '#ef4444', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                        Connection Failed
                    </div>
                    <div style={{ color: '#b6c6e3', fontSize: 14, textAlign: 'center', maxWidth: 400, marginBottom: 24 }}>
                        {error}
                    </div>
                    <div style={{ color: '#64748b', fontSize: 12 }}>
                        Redirecting to your profile...
                    </div>
                </>
            )}
        </div>
    );
};

export default OAuthCallback;









